/**
 * @Author: Susana García Rodríguez
 * @JobTitle : Frontend
 * @Email : sgarcia@ibl.mx
 *
 */

import {EMPTY_VALUES, hasValidDate} from '../../../../../Apis/functions';
import {
    CheckIconsOptions,
    CrossIconsOptions,
    IconMealTable,
    IconRequestRemaining,
    IconUnderReview,
} from '../../../../../components/buttons/IconButtons';
import {colors} from '../../../../../enums/colors';
import {STATUS_MODEL} from '../../../../../enums/statusEnum';
import { DishCategoryPayload } from './../../Menu/Model/Model';


//Auxiliar functions


export const hasDescription = (desc) => {
    return (desc?.trim() || '') !== '';
};

export const hasValidNumberOfMeals = (mealsNumber) => {
    const num = Number(mealsNumber);
    return Number.isFinite(num) && num >= ExtraMealsRange.min && num <= ExtraMealsRange.max;
};

/**
 * Extra Meal model for Foodie with helper methods.
 */
export const FOODIE_EXTRA_MEAL = {
    servingDate: null,
    description: '',
    requestingId: null,
    id: null,
    mealsNumber: '',
    usersId: [],
    departmentIds: [],
    isExtraRequest: true,
    mealTimeId:null,
    /**
     * Validate if the object has the required data to create a new record.
     * @returns {boolean}
     */
    isValid(extraMealType, selected) {
        if (!hasValidDate(this.servingDate)) return false;
        if (!hasDescription(this.description)) return false;

        if ( extraMealType === EXTRA_MEAL_TYPE_MODEL.PERSONAL &&
            (!this.mealsNumber || !hasValidNumberOfMeals(this.mealsNumber))
        ) {
            return false;
        }

        if ([EXTRA_MEAL_TYPE_MODEL.FOR_EMPLOYEES, EXTRA_MEAL_TYPE_MODEL.FOR_DEPARTMENTS].includes(extraMealType) &&
            selected.length === 0) {
            return false;
        }

        return true;
    },

    /**
     * Format the object for registration (API payload).
     * @returns {Object}
     */
    toRegister(extraMealType, selected) {
        const base = {
            id: this.id,
            servingDate: this.servingDate ? new Date(this.servingDate).getTime() : null,
            description: this.description,
            userId: this.requestingId,
            isExtraRequest: this.isExtraRequest,
            mealTimeId: this.mealTimeId
        };

        if (extraMealType === EXTRA_MEAL_TYPE_MODEL.FOR_EMPLOYEES) {
            return { ...base, userIds : selected.map(e => e.id) };
        }else if (extraMealType === EXTRA_MEAL_TYPE_MODEL.FOR_DEPARTMENTS) {
            return { ...base, departmentIds : selected.map(e => e.id) };
        } else {
            return { ...base, number: this.mealsNumber };
        }
    },

    /**
     * Format the object for update (API payload).
     * @returns {Object}
     */
    toUpdate() {
        return {
            id: this.id,
            servingDate: this.servingDate ? new Date(this.servingDate).getTime() : null,
            description: this.description,
            mealTimeId: this.mealTimeId
        };
    },

    /**
     * Set the object properties based on the provided data.
     */
    set: function (data) {
        return {
            ...this,
            servingDate: data.servingDate,
            description: data.description,
            requestingId: data.requestingId,
            id: data.id,
            usersId: data.usersId || [],
            departmentIds: data.departmentIds || [],
            isExtraRequest : data.isExtraRequest,
            mealTimeId: data.mealTimeId
        };
    }
};

export const REQUEST_STATUS = {
    /**
     * Approved status:
     * If there is no approval flow, the extra request is marked as approved by default.
     */
    APPROVED: {
        id: STATUS_MODEL.APPROVED,
        titleTrans: 'approved_meals',
        classCard: 'card-success-300-100',
        fontColor: 'font-color-success-600',
        iconColor: 'icon-color-success-600',
        color: colors.success700,
        border: colors.success150,
        background: colors.success100,
        Icon: (props) => <CheckIconsOptions width={46} height={46} {...props} />,
    },
    /**
     * Pending Review:
     * If the extra request is created and there's an approval flow, it goes to "Pending Review".
     */
    PENDING_REVIEW: {
        id: STATUS_MODEL.PENDING_REVIEW,
        titleTrans: 'pending_review',
        classCard: 'card-warning-300-100',
        fontColor: 'font-color-warning-700',
        iconColor: 'icon-color-warning-700',
        color: colors.warning600,
        border: colors.warning300,
        background: colors.warning100,
        Icon: IconUnderReview,
    },
    /**
    * In Progress:
    * When the extra request is under review, it is marked as "In Progress".
    */
    IN_PROGRESS: {
        id: STATUS_MODEL.IN_PROGRESS,
        titleTrans: 'in_progress',
        classCard: 'card-shades-400-100',
        fontColor: 'font-color-shades-600',
        iconColor: 'icon-color-shades-600',
        color: colors.shades600,
        border: colors.shades300,
        background: colors.shades100,
        Icon: IconRequestRemaining,
    },
    /**
    * Denied:
    * When the extra request is rejected, it is marked as "Denied".
    */
    DENIED: {
        id: STATUS_MODEL.DENIED,
        titleTrans: 'denied',
        classCard: 'card-error-code-300-100',
        fontColor: 'font-color-error-700',
        iconColor: 'icon-color-error-code-700',
        color: colors.errorCode700,
        border: colors.errorCode300,
        background: colors.errorCode100,
        Icon: (props) => <CrossIconsOptions width={42} height={42} {...props} />,
    },
};
/**
 * DISABLED_CARD_STYLE
 * Default style for disabled request cards.
 */
export const DISABLED_CARD_STYLE = {
    classCard: 'card-shades-400-100',
    fontColor: 'font-color-shades-300',
    iconColor: 'icon-color-shades-300',
};

/**
 * Function to calculate the count of each statusId from the search results
 * and return an array of objects with name (statusId) and value (count).
 *
 * @param {Object} searchResults - Object containing a "content" array with items having statusId.
 * @returns {Array} Array of objects in the format [{ name: statusId, value: count }, ...].
 */
export const getStatusData = (searchResults) => {
    if (!Array.isArray(searchResults?.content)) return [];

    const statusCounts = searchResults.content.reduce((acc, item) => {
        const status = item.statusId;
        acc[status] = (acc[status] || 0) + 1;
        return acc;
    }, {});

    return Object.entries(statusCounts).map(([name, value]) => ({
        name,
        value,
    }));
};

//Ranges for selecting extra meals
export const ExtraMealsRange = {
    min: 1,
    max: 99
}

/**
 * Downloads the QR code as a PNG file from a given container reference.
 *
 * @param {Object} qrContainerRef - A React ref pointing to the container that holds the QR code canvas.
 * @param {string} qrName - The desired filename for the downloaded QR code (without extension).
 */
export const downloadQrFile = (qrContainerRef, qrName) => {
    if (!qrContainerRef.current) {
        return;
    }
    const canvas = qrContainerRef.current.querySelector('canvas');
    if (!canvas) {
        return;
    }
    const image = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = image;
    link.download = qrName + '.png';
    link.click();
};

// Renders a list of meals for a given item.
  export const renderMeals = (item) => {
       const keys = Object.values(DishCategoryPayload);

        const meals = keys.map((key) => item[key]).filter((meal) => meal);

        if (meals.length === 0) return EMPTY_VALUES.DEFAULT;

        return meals.map((meal) => (
            <div key={meal.id} className='d-flex align-items-center'>
                <div className='mr-2'>
                    <IconMealTable />
                </div>
                {meal.name}
            </div>
        ));
    };

  export const EXTRA_MEAL_TYPE_MODEL = {
      PERSONAL: 'PERSONAL',
      FOR_EMPLOYEES: 'FOR_EMPLOYEES',
      FOR_DEPARTMENTS: 'FOR_DEPARTMENTS'
  }
